import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import BackArrowIcon from 'assets/actionPlan/backArrow.svg';
import CheckIcon from 'assets/actionPlan/check.svg';
import DeleteIcon from 'assets/actionPlan/delete.svg';
import EditIcon from 'assets/actionPlan/edit.svg';
import SalesTargetIcon from 'assets/actionPlan/sales-target.svg';
import searchIcon from 'assets/actionPlan/search.svg';
import UpComingIcon from 'assets/actionPlan/upcoming.svg';
import VectorIcon from 'assets/actionPlan/vector.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import ResetButton from 'common-ui/resetButton';
import { AsyncSelect } from 'common-ui/select';
import { Table as CustomTable } from 'common-ui/table';
import loadIcon from 'common-ui/table/loading.svg';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  getActionById,
  getAssignments,
  removeAssignment,
} from '../../../features/actions/actionPlanActions';
import {
  selectActionLoading,
  selectAssignmentLoading,
  selectAssignmentsList,
  selectSelectedAction,
  selectTotalElements,
  selectTotalPages,
} from '../../../features/actions/actionPlanSlice';
import {
  ACTION_OPTIONS,
  ASSIGNEE_OPTIONS,
  DURATION_TYPE_OPTIONS,
  ESTIMATION_OPTIONS,
  STATUS_OPTIONS,
} from '../ActionPlanConstans';
import ActionEditModal from '../actionEditModal/ActionEditModal';
import AssignmentDetails from '../assignmentDetails/AssignmentDetails';
import {
  AlertIcon,
  AssignmentCard,
  BackIcon,
  ButtonWrapper,
  CardHeader,
  CheckBg,
  Container,
  DeleteActionIcon,
  EditImg,
  ErrorDate,
  FormWrapper,
  InfoGroup,
  InfoGroupLabel,
  InfoGroupSpan,
  InfoGroupText,
  LoadContainer,
  LoadingIcon,
  SearchAndFilter,
  SettingsIconBlock,
  SettingsImg,
  SettingsSubTitle,
  SettingsTitle,
  SettingsTitleBlock,
  SettingsTopbar,
  StatusBadge,
  UpcomingImg,
  UserColor,
} from './SalesTarget.styles';
import useSearchData from './useSearchData';

dayjs.extend(utc);
const tomorrow = dayjs().add(1, 'day').startOf('day');

const getColumns = ({ setSelectedRow, setModalType, handleNameClick, navigate }) => [
  {
    title: 'Assignee Name',
    dataIndex: 'assigneeName',
    key: 'assigneeName',
    sortable: true,
    render: (text, row) => <UserColor onClick={() => handleNameClick(row)}>{text}</UserColor>,
  },
  {
    title: 'Assignee Type',
    dataIndex: 'scope',
    key: 'scope',
    render: (value) => value?.charAt(0) + value?.slice(1).toLowerCase(),
  },
  {
    title: 'Action Type',
    dataIndex: 'actionType',
    key: 'actionType',
    render: (value) => {
      const label = value?.charAt(0) + value?.slice(1).toLowerCase();

      const typeMap = {
        PRIMARY: 'active',
        SECONDARY: 'inactive',
      };

      return (
        <StatusBadge className="action-type" $type={typeMap[value] || 'default'}>
          {label}
        </StatusBadge>
      );
    },
  },
  {
    title: 'Estimation',
    dataIndex: 'estimationValue',
    key: 'estimationValue',
    render: (value, row) => {
      const type = row.estimationType;

      if (type === 'FINANCIAL') return 'FINANCIAL';
      if (type === 'QUANTITY') return 'QUANTITY';
      if (type === 'BOTH') return 'BOTH';

      return value;
    },
  },
  {
    title: 'Duration',
    dataIndex: 'periodType',
    key: 'periodType',
    render: (value) => value?.toUpperCase(),
  },
  {
    title: 'KPI Target',
    key: 'kpiTarget',
    render: (_, row) => {
      const q = row.quantityTarget ?? '-';
      const f = row.financialTarget ?? '-';

      if (row.estimationType === 'BOTH') {
        return `${q} / ${f}`;
      }

      if (row.estimationType === 'QUANTITY') {
        return `${q}`;
      }

      if (row.estimationType === 'FINANCIAL') {
        return `${f}`;
      }

      return '-';
    },
  },
  {
    title: 'Effective date',
    dataIndex: 'effectiveDate',
    key: 'effectiveDate',
    render: (value) => value || '-',
  },
  {
    title: 'unAssignment date',
    dataIndex: 'removalEffectiveDate',
    key: 'removalEffectiveDate',
    render: (value) => value || '-',
  },
  {
    title: 'Status',
    dataIndex: 'active',
    key: 'active',
    render: (active) => (
      <StatusBadge $type={active ? 'active' : 'inactive'}>
        {active ? 'Active' : 'Inactive'}
      </StatusBadge>
    ),
  },
  {
    title: 'Action',
    key: 'action',
    render: (_, row) => {
      const isDisabled = !row.active;
      const canEdit =
        row.effectiveDate &&
        dayjs(row.effectiveDate).startOf('day').isAfter(dayjs().startOf('day'));
      return (
        <>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <DeleteActionIcon
              src={DeleteIcon}
              $isDisabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  setSelectedRow(row);
                  setModalType('confirm');
                }
              }}
            />

            <img
              src={EditIcon}
              alt="edit"
              style={{
                opacity: canEdit ? 1 : 0.4,
                cursor: canEdit ? 'pointer' : 'not-allowed',
              }}
              onClick={() => {
                if (!canEdit) return;

                navigate(`/action-plan/edit-assignment/${row.id}`);
              }}
            />
          </div>
        </>
      );
    },
  },
];

const SalesTarget = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode;
  const { actionId } = useParams();
  const dispatch = useDispatch();
  const { searchData, setSearchData, resetSearchData } = useSearchData();

  const data = useSelector(selectSelectedAction);
  const loading = useSelector(selectActionLoading);
  const assignments = useSelector(selectAssignmentsList) || [];
  const assignmentsLoading = useSelector(selectAssignmentLoading);
  const totalCount = useSelector(selectTotalElements);
  const totalPages = useSelector(selectTotalPages);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [date, setDate] = useState(null);
  const [dateError, setDateError] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const handleOpenEditModal = () => setIsEditModalOpen(true);
  const handleCloseEditModal = () => setIsEditModalOpen(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const isFutureDisable = data?.disableEffectiveDate
    ? new Date(data.disableEffectiveDate) > new Date()
    : false;

  const showUpcomingStatus = data?.enabled === false && isFutureDisable;
  const [isFocused, setIsFocused] = useState(false);
  const [selectedActionForEdit, setSelectedActionForEdit] = useState(null);

  const fetchAssignments = React.useCallback(() => {
    dispatch(
      getAssignments({
        actionId,
        page: searchData.page ? searchData.page - 1 : 0,
        size: searchData.limit ?? 10,
        ...(searchData.search && { name: searchData.search }),
        ...(searchData.assigneeType && { scope: searchData.assigneeType.toUpperCase() }),
        ...(searchData.estimationType && {
          estimationType: searchData.estimationType.toUpperCase(),
        }),
        ...(searchData.durationType && { periodType: searchData.durationType.toUpperCase() }),
        ...(searchData.actionType && { actionType: searchData.actionType.toUpperCase() }),
        ...(searchData.status === 'true' && { active: true }),
        ...(searchData.status === 'false' && { active: false }),
      })
    );
  }, [dispatch, actionId, searchData]);

  const handleNameClick = (row) => {
    setSelectedAssignee(row);
    setIsDetailsModalOpen(true);
  };

  const handleUnassign = async (row) => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 1);

    try {
      const result = await dispatch(
        removeAssignment({
          id: row.id,
          effectiveDate: futureDate.toISOString(),
        })
      );

      if (result?.meta?.requestStatus === 'fulfilled') {
        fetchAssignments();
      }
    } catch (e) {
      console.error(e);
    }
  };
  const confirmRemove = async () => {
    if (!selectedRow) return;

    if (!date) {
      setDateError('Date is required');
      return;
    }

    const selected = dayjs(date).startOf('day');
    const tomorrow = dayjs().add(1, 'day').startOf('day');

    if (selected.isBefore(tomorrow, 'day')) {
      setDateError('Effective date must be from tomorrow');
      return;
    }

    setDateError('');

    try {
      const result = await dispatch(
        removeAssignment({
          id: selectedRow.id,
          effectiveDate: selected.utc(true).toISOString(),
        })
      );

      if (result?.meta?.requestStatus === 'fulfilled') {
        fetchAssignments();
        closeModal();
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (actionId) {
      dispatch(getActionById(actionId));
    }
  }, [actionId, dispatch]);

  useEffect(() => {
    if (actionId) fetchAssignments();
  }, [actionId, fetchAssignments]);

  const columns = React.useMemo(
    () => getColumns({ setSelectedRow, setModalType, handleNameClick, navigate }),
    []
  );

  const handleBack = () => {
    navigate('/action-plan/settings');
  };

  const handleFilterChange = (field, value) => {
    setSearchData({ [field]: value || '', page: 1 });
  };

  const handleSearchChange = (e) => {
    setSearchData({ search: e.target.value });
  };
  const openEffectiveDateModal = (row) => {
    setSelectedRow(row);
    setModalType('effectiveDate');
  };
  const closeModal = () => {
    setSelectedRow(null);
    setModalType(null);
    setDate(null);
  };
  const handleEditSuccess = () => {
    dispatch(getActionById(actionId));
  };

  if (loading && !data) {
    return (
      <Container>
        <LoadContainer>
          <LoadingIcon src={loadIcon} alt="Loading..." />
        </LoadContainer>
      </Container>
    );
  }

  const getAssigneeType = (row) => {
    if (!row) return 'EMPLOYEE';

    const scope = row.scope?.toUpperCase();

    const isDepartment =
      scope === 'DEPARTMENT' ||
      row.departmentId != null ||
      (row.employeeId == null && row.departmentId != null);

    return isDepartment ? 'DEPARTMENT' : 'EMPLOYEE';
  };

  return (
    <Container>
      <SettingsTopbar>
        <BackIcon src={BackArrowIcon} onClick={handleBack} />
        <SettingsIconBlock>
          <SettingsImg src={SalesTargetIcon} />
        </SettingsIconBlock>
        <SettingsTitleBlock>
          <SettingsTitle>{data?.actionName}</SettingsTitle>
          <SettingsSubTitle>Assignment details</SettingsSubTitle>
        </SettingsTitleBlock>
      </SettingsTopbar>
      <AssignmentCard>
        {/* <EditImg
          src={EditIcon}
          onClick={() => {
            setSelectedActionForEdit(data);
            handleOpenEditModal();
          }}
        /> */}
        <EditImg
          src={EditIcon}
          $disabled={!data?.enabled}
          onClick={() => {
            if (!data?.enabled) return;

            setSelectedActionForEdit(data);
            handleOpenEditModal();
          }}
        />
        <CardHeader className="card-header">
          {data?.actionName && (
            <InfoGroup>
              <InfoGroupLabel>Action Name:</InfoGroupLabel>
              <InfoGroupSpan>{data?.actionName}</InfoGroupSpan>
            </InfoGroup>
          )}
          {data?.description && (
            <InfoGroup>
              <InfoGroupLabel>Description:</InfoGroupLabel>
              <InfoGroupText>{data?.description}</InfoGroupText>
            </InfoGroup>
          )}
          {data?.enabled !== undefined && (
            <InfoGroup>
              <InfoGroupLabel>Current Status:jkll</InfoGroupLabel>
              <StatusBadge $type={data?.enabled ? 'active' : 'inactive'}>
                {data?.enabled ? 'Active' : 'Inactive'}
              </StatusBadge>
            </InfoGroup>
          )}
          {data?.disableEffectiveDate && (
            <InfoGroup>
              <InfoGroupLabel>Disable Effective Date:</InfoGroupLabel>
              <InfoGroupSpan>{data?.disableEffectiveDate}</InfoGroupSpan>
            </InfoGroup>
          )}
          {data?.activeAssignmentsCount !== undefined && (
            <InfoGroup>
              <InfoGroupLabel>Active Assignments:</InfoGroupLabel>
              <InfoGroupSpan>{data?.activeAssignmentsCount}</InfoGroupSpan>
            </InfoGroup>
          )}
          <ActionEditModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSuccess={handleEditSuccess}
            selectedAction={selectedActionForEdit}
          />
        </CardHeader>
        {(data?.pendingActionName ||
          data?.pendingDescription ||
          data?.updateEffectiveDate ||
          showUpcomingStatus) && (
          <CardHeader>
            {data?.pendingActionName && data.pendingActionName !== data.actionName && (
              <InfoGroup className="upcoming-img">
                <UpcomingImg src={UpComingIcon} />
                <InfoGroupLabel>Pending Name:</InfoGroupLabel>
                <InfoGroupSpan>{data.pendingActionName}</InfoGroupSpan>
              </InfoGroup>
            )}

            {data?.pendingDescription && data.pendingDescription !== data.description && (
              <InfoGroup className="upcoming-img">
                <UpcomingImg src={UpComingIcon} />
                <InfoGroupLabel>Pending Description:</InfoGroupLabel>
                <InfoGroupSpan>{data.pendingDescription}</InfoGroupSpan>
              </InfoGroup>
            )}
            {showUpcomingStatus && (
              <InfoGroup className="upcoming-img">
                <UpcomingImg src={UpComingIcon} />
                <InfoGroupLabel>Upcoming Status:</InfoGroupLabel>
                <StatusBadge $type="inactive">Inactive</StatusBadge>
              </InfoGroup>
            )}
            {data?.updateEffectiveDate && (
              <InfoGroup>
                <InfoGroupLabel>Update Effective Date:</InfoGroupLabel>
                <InfoGroupSpan>{data.updateEffectiveDate}</InfoGroupSpan>
              </InfoGroup>
            )}
          </CardHeader>
        )}
      </AssignmentCard>
      <SearchAndFilter>
        <Input
          label="Search by name"
          placeholder=""
          width="300px"
          icon={isFocused || searchData.search ? null : searchIcon}
          value={searchData.search}
          onChange={handleSearchChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          clearable
          onClear={() => setSearchData({ search: '' })}
        />
        <AsyncSelect
          label="Assignee type"
          placeholder="Assignee type"
          loadOptions={(inputValue) => Promise.resolve(ASSIGNEE_OPTIONS)}
          defaultOptions={ASSIGNEE_OPTIONS}
          value={ASSIGNEE_OPTIONS.find((opt) => opt.value === searchData.assigneeType) || null}
          onChange={(opt) => handleFilterChange('assigneeType', opt?.value)}
          isClearable
        />
        <AsyncSelect
          label="Estimation type"
          placeholder="Estimation type"
          loadOptions={(inputValue) => Promise.resolve(ESTIMATION_OPTIONS)}
          defaultOptions={ESTIMATION_OPTIONS}
          value={
            ESTIMATION_OPTIONS.find((opt) => opt.value === searchData.estimationType) || null
          }
          onChange={(opt) => handleFilterChange('estimationType', opt?.value)}
          isClearable
        />
        <AsyncSelect
          label="Duration type"
          placeholder="Duration type"
          loadOptions={(inputValue) => Promise.resolve(DURATION_TYPE_OPTIONS)}
          defaultOptions={DURATION_TYPE_OPTIONS}
          value={
            DURATION_TYPE_OPTIONS.find((opt) => opt.value === searchData.durationType) || null
          }
          onChange={(opt) => handleFilterChange('durationType', opt?.value)}
          isClearable
        />
        <AsyncSelect
          label="Action type"
          placeholder="Action type"
          loadOptions={(inputValue) => Promise.resolve(ACTION_OPTIONS)}
          defaultOptions={ACTION_OPTIONS}
          value={ACTION_OPTIONS.find((opt) => opt.value === searchData.actionType) || null}
          onChange={(opt) => handleFilterChange('actionType', opt?.value)}
          isClearable
        />
        <AsyncSelect
          label="Status"
          placeholder="Status"
          loadOptions={() => Promise.resolve(STATUS_OPTIONS)}
          defaultOptions={STATUS_OPTIONS}
          value={STATUS_OPTIONS.find((opt) => opt.value === searchData.status) || null}
          onChange={(opt) => handleFilterChange('status', opt?.value)}
        />
        <ResetButton onClick={resetSearchData} title="Reset" />
      </SearchAndFilter>
      <CustomTable
        data={assignments}
        columns={columns}
        loading={assignmentsLoading}
        currentPage={searchData.page}
        totalPages={totalPages}
        count={totalCount}
        onPaginationChange={(page) => setSearchData({ page })}
        handleRowCountChange={(size) => setSearchData({ limit: size, page: 1 })}
        getRowColor={(row) =>
          row.scope?.toUpperCase() === 'DEPARTMENT' ? '#296BCD1A' : undefined
        }
      />
      <AssignmentDetails
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        assignmentId={selectedAssignee?.id}
        onSuccess={fetchAssignments}
      />
      <Modal
        isOpen={!!modalType}
        onClose={closeModal}
        width="600px"
        $centered
        footer={false}
        className={modalType === 'effectiveDate' ? 'effective-date-modal' : ''}
      >
        {modalType === 'confirm' && (
          <FormWrapper>
            <CheckBg>
              <AlertIcon src={CheckIcon} />
            </CheckBg>

            <SettingsTitle className="confirmation">Confirmation</SettingsTitle>

            <SettingsSubTitle className="confirmation-sub">
              {getAssigneeType(selectedRow) === 'DEPARTMENT'
                ? 'Are you sure you want to unassign this department? Changes will apply from the effective date.'
                : 'Are you sure you want to unassign this employee? Changes will apply from the effective date.'}
            </SettingsSubTitle>

            <ButtonWrapper>
              <Button className="cancel" outlined onClick={closeModal} width="45%">
                Cancel
              </Button>

              <Button
                className="confirm"
                secondary
                onClick={() => {
                  if (!selectedRow) return;
                  openEffectiveDateModal(selectedRow);
                }}
                width="45%"
              >
                Confirm
              </Button>
            </ButtonWrapper>
          </FormWrapper>
        )}
        {modalType === 'effectiveDate' && (
          <FormWrapper className="date-effective">
            <SettingsTitle className="confirmation date-title">Effective date</SettingsTitle>
            <CustomDatePicker
              label="Effective date*"
              minDate={tomorrow}
              value={date}
              onChange={(val) => {
                setDate(val);
                setDateError('');
              }}
              slotProps={{
                actionBar: {
                  actions: [],
                },
              }}
            />

            {dateError && (
              <ErrorDate>
                <AlertIcon src={VectorIcon} />
                {dateError}
              </ErrorDate>
            )}

            <ButtonWrapper>
              <Button className="cancel" outlined onClick={closeModal}>
                Cancel
              </Button>

              <Button className="confirm" primary onClick={confirmRemove}>
                Submit
              </Button>
            </ButtonWrapper>
          </FormWrapper>
        )}
      </Modal>
    </Container>
  );
};

export default SalesTarget;
